import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Formik } from 'formik'
import { uniq, flatten, uniqWith, isEqual, map, entries, minBy, maxBy } from 'lodash'

import Api from 'services/Api'
import { MultiSelect, Select, DateTimePicker, Button, Form, Checkbox } from 'components/Forms'
import { Collapsable } from 'components/UI'
import { setContent, setCount, setSelection, setIsLoading } from 'store/actions/tables'
import { createErrorNotification } from 'store/actions/notifications'
import { sortBasedOnProjectStatus } from 'services/Projects'
import { t } from 'translations'
import { useAttributes } from 'Api'

// This has to be in a component so it doesn't get reset every time
// the parent re-renders
const EmployeeSelectionToolCriteriaForm = ({ events, template, appliedDates }) => {
  const { enableAttributes, projectRestrictions: projectRestrictionsEnabled } = useSelector(state => state.settings)
  const { sortsById, filtersById } = useSelector(state => state.resources)

  const dispatch = useDispatch()
  const { sorts, filters, positionsById, projectsById, tags, tagsById, projects } = useSelector(state => state.resources)
  const { data: attributes } = useAttributes(data => data.attributes)

  // Extract information from the template
  const defaultRegions = template?.defaultShiftTags.filter(id => tagsById[id].type === 'regional')
  let project = template.projects?.[0]

  // Get the required regions from the selected event(s)
  const requiredRegions = uniq(
    flatten(
      events.map(({ tags }) =>
        [...defaultRegions, ...tags].map(tagId => {
          if (tagsById[tagId]?.type === 'regional') return tagId
          if (tagsById[tagId]?.regionalTag) return tagsById[tagId].regionalTag
          return null
        })
      )
    )
  ).filter(Boolean)

  // Get the required project(s) depending if there is a project linked to the template or in the selected event(s)
  const requiredProjects = project ? [project] : flatten(uniq(events.map(({ projects }) => projects)))

  // Get all the project required attributes
  const projectAttributes = project
    ? projectsById[project]?.requiredAttributes
    : requiredProjects.length
    ? uniqWith(flatten(requiredProjects.map(projectId => projectsById[projectId]?.requiredAttributes)), isEqual)
    : []

  // Map each attribute to it's highest required rank if there is one
  const requiredAttributesMap = [...flatten(events.map(({ positionId }) => positionsById[positionId]?.attributes || [])), ...projectAttributes].reduce(
    (acc, attribute) => {
      if (attribute) acc[attribute.attributeId] = attribute.rank
      return acc
    },
    {}
  )

  // Format the requiredAttributes to get an object with attributeId and rank
  const requiredAttributes = map(entries(requiredAttributesMap), ([value, rank]) => ({ value, rank }))

  const loadUsers = async ({ datesToCheck, attributes, regions, projects, selectedSort, selectedFilter, filterEligibleEmployees }) => {
    setIsLoading('st-employee-selection-tool', true)

    const sorts = sortsById[selectedSort] ? sortsById[selectedSort].usedSorts : []
    const { flags = {}, options = {} } = filtersById[selectedFilter] || {}

    let dynamicFlags = {}
    let dynamicOptions = {}

    if (enableAttributes) {
      dynamicFlags.missingAttributes = true
      dynamicFlags.attributes = 1

      // Grab rank from selected position if there is one
      dynamicOptions.requiredAttributes = attributes.map(({ value, rank }) => ({ attributeId: value, rank: rank || 0 }))
      dynamicOptions.filterByAttributesFirst = true
    }

    dynamicOptions.requiredPositions = events.map(({ positionId }) => positionId)

    if (events.length) {
      dynamicOptions.dateSpecificStart = minBy(events, 'start')?.start
      dynamicOptions.dateSpecificEnd = maxBy(events, 'end')?.end
    }

    if (sorts.some(({ id }) => id === 'projectExperienceSort')) dynamicOptions.projects = project ? [project] : []

    // Reset the selection when we change the filter
    setSelection('st-employee-selection-tool', {})
    const params = {
      fields: { _id: 1, firstName: 1, lastName: 1, employeeId: 1, attributes: 1, projects: 1, regionalTags: 1, specificRestrictions: 1 },
      flagConfig: {
        dates: datesToCheck,
        flags: { ...flags, ...dynamicFlags },
        options: { ...options, ...dynamicOptions },
        sorts
      },
      useNewReturn: true
    }
    // Filters from the form
    if (regions.length) params.regionalTags = regions
    if (projects.length) params.projects = projects

    let { result, success } = await Api.post(`/users/flags`, params)

    if (!success) {
      dispatch(createErrorNotification())
      setContent('st-employee-selection-tool', [])
      setCount('st-employee-selection-tool', 0)
    } else {
      // if projectRestrictions setting is enabled and component is being accessed from a project
      if (projectRestrictionsEnabled && project) {
        result = sortBasedOnProjectStatus(result, project)
      }
      // If filterEligibleEmployees is checked, filter only eligible employees
      if (filterEligibleEmployees) result = result.filter(({ flags }) => Object.values(flags.dateFlags).every(({ isEligible }) => isEligible))

      setContent('st-employee-selection-tool', result)
      setCount('st-employee-selection-tool', result.length)
    }

    setIsLoading('st-employee-selection-tool')
  }

  return (
    <Collapsable type="shiftOptions" title="manager.scheduleTemplates.employeeSelectionTool.LOAD_EMPLOYEES" color="bg-light" show>
      <Formik
        enableReinitialize={true}
        initialValues={{
          attributes: requiredAttributes,
          regions: requiredRegions,
          projects: requiredProjects,
          datesToCheck: appliedDates,
          selectedSort: '',
          selectedFilter: '',
          filterEligibleEmployees: false
        }}
        onSubmit={loadUsers}
      >
        {({ isSubmitting }) => {
          return (
            <Form>
              <DateTimePicker module="manager.scheduleTemplates.employeeSelectionTool.form" name="datesToCheck" type="dateMultiple" isClearable required />
              {enableAttributes ? (
                <MultiSelect
                  module="manager.scheduleTemplates.employeeSelectionTool.form"
                  name="attributes"
                  options={flatten(
                    attributes.map(({ _id, name, type, levels }) =>
                      levels.map(level => ({
                        value: _id,
                        label: `${name} (${level.name})`,
                        rank: level.rank,
                        icon: {
                          restriction: 'fas fa-exclamation-triangle',
                          skill: 'fas fa-graduation-cap',
                          requirement: 'fas fa-clipboard-check',
                          alert: 'fas fa-exclamation-triangle'
                        }[type]
                      }))
                    )
                  )}
                  isMulti
                  returnFullValue
                />
              ) : null}
              <MultiSelect
                module="manager.scheduleTemplates.employeeSelectionTool.form"
                name="regions"
                options={tags
                  .filter(({ type }) => type === 'regional')
                  .map(({ _id, name, icon }) => {
                    return {
                      value: _id,
                      label: `${name}`,
                      icon
                    }
                  })}
                isMulti
              />
              <MultiSelect
                module="manager.scheduleTemplates.employeeSelectionTool.form"
                name="projects"
                options={projects.map(({ _id, name, code }) => {
                  return {
                    value: _id,
                    label: `${name} [${code}]`
                  }
                })}
                isClearable
                isMulti
              />
              {sorts.length ? (
                <Select
                  module="manager.scheduleTemplates.employeeSelectionTool.form"
                  name="selectedSort"
                  options={[{ _id: '', name: t('NO_SORT') }, ...sorts]}
                />
              ) : null}
              {filters.length ? (
                <Select
                  module="manager.scheduleTemplates.employeeSelectionTool.form"
                  name="selectedFilter"
                  options={[{ _id: '', name: t('NO_FILTER') }, ...filters]}
                />
              ) : null}
              <Checkbox name="filterEligibleEmployees" module="manager.scheduleTemplates.employeeSelectionTool.form" />
              <div className="mt-2">
                <Button className="btn btn-primary mr-2" type="submit" isSubmitting={isSubmitting} text="SUBMIT" />
              </div>
            </Form>
          )
        }}
      </Formik>
    </Collapsable>
  )
}

export default EmployeeSelectionToolCriteriaForm
